import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Frame, More, useDestinations } from './frame/Frame';
import { onToast } from './lib/api';
import { useSession } from './lib/session';
import { Inventory } from './screens/inventory/Inventory';
import { KanbanCards } from './screens/inventory/KanbanCard';
import { PurchaseOrderDocument } from './screens/inventory/PurchaseOrder';
import { Home, PipelinePage } from './screens/Home';
import { OpportunityPage } from './screens/pipeline/Opportunity';
import { NeedsYouPage } from './screens/pipeline/NeedsYou';
import { PlacePage } from './screens/pipeline/Place';
import { StagePage } from './screens/pipeline/Stage';
import { WonLostPage } from './screens/pipeline/WonLost';
import { Jobs } from './screens/JobList';
import { Library } from './screens/Library';
import { People } from './screens/People';
import { Plan } from './screens/Plan';
import { RecipeEditor } from './screens/RecipeEditor';
import { Scan } from './screens/Scan';
import { Settings } from './screens/Settings';
import { Shop } from './screens/Shop';
import { NotSetUp, SignIn } from './screens/SignIn';
import { Traveler } from './screens/Traveler';
import { Verify } from './screens/Verify';
import { AllFiles } from './screens/job/Files';
import { Workspace } from './screens/job/Workspace';
import { Empty, Loading } from './ui/kit';

function Toasts() {
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => {
    let timer = 0;
    onToast((m) => {
      setMessage(m);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setMessage(null), 4000);
    });
  }, []);
  return message ? (
    <div className="toast" role="status">
      {message}
    </div>
  ) : null;
}

/** A screen shows only to someone whose permissions include it. */
function Gate({ any, children }: { any: string[]; children: ReactNode }) {
  const { can } = useSession();
  return any.some(can) ? <>{children}</> : <Empty>This screen is not part of your role.</Empty>;
}

function Landing() {
  const { can } = useSession();
  const dests = useDestinations();
  if (can('screen.home')) return <Home />;
  // people without Home land on the first place they can use: the shop lands on the board
  const first = dests.find((d) => d.key === 'shop') ?? dests[0];
  return first ? <Navigate to={first.to} replace /> : <Empty>Nothing has been shared with your role yet.</Empty>;
}

export function App() {
  const s = useSession();
  if (s.loading) return <div style={{ padding: 24 }}><Loading what="Starting ZeroGap" /></div>;
  if (s.needsSignIn) return <SignIn />;
  if (s.notSetUp) return <NotSetUp onSignOut={() => void s.signOut()} />;
  return (
    <>
      <Frame>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/jobs" element={<Gate any={['screen.jobs']}><Jobs /></Gate>} />
          <Route path="/jobs/:jobId" element={<Gate any={['screen.jobs']}><Workspace /></Gate>} />
          <Route path="/opportunities/:id" element={<Gate any={['screen.pipeline', 'screen.jobs', 'screen.home']}><OpportunityPage /></Gate>} />
          <Route path="/pipeline" element={<Gate any={['screen.pipeline', 'screen.home']}><PipelinePage /></Gate>} />
          <Route path="/pipeline/stage/:key" element={<Gate any={['screen.pipeline', 'screen.home']}><StagePage /></Gate>} />
          <Route path="/pipeline/needs-you" element={<Gate any={['screen.pipeline', 'screen.home']}><NeedsYouPage /></Gate>} />
          <Route path="/pipeline/won-lost" element={<Gate any={['screen.pipeline', 'screen.home']}><WonLostPage /></Gate>} />
          <Route path="/pipeline/place" element={<Gate any={['screen.pipeline', 'screen.home']}><PlacePage /></Gate>} />
          <Route path="/library" element={<Gate any={['screen.library']}><Library /></Gate>} />
          <Route path="/library/catalog/:id" element={<Gate any={['screen.library']}><RecipeEditor /></Gate>} />
          <Route path="/library/:table" element={<Gate any={['screen.library']}><Library /></Gate>} />
          <Route path="/verify/:table/:id" element={<Gate any={['screen.jobs', 'screen.library']}><Verify /></Gate>} />
          <Route path="/shop" element={<Gate any={['screen.board_owner', 'screen.board_tablet']}><Shop /></Gate>} />
          <Route path="/shop/cards/:cardId/traveler" element={<Gate any={['screen.board_owner', 'screen.board_tablet']}><Traveler /></Gate>} />
          <Route path="/scan" element={<Gate any={['screen.scan']}><Scan /></Gate>} />
          <Route path="/files" element={<Gate any={['screen.files']}><AllFiles /></Gate>} />
          <Route path="/inventory" element={<Gate any={['screen.purchasing', 'screen.receiving']}><Inventory /></Gate>} />
          <Route path="/inventory/orders/:id" element={<Gate any={['screen.purchasing']}><PurchaseOrderDocument /></Gate>} />
          <Route path="/inventory/cards/:materialId" element={<Gate any={['screen.purchasing', 'screen.receiving']}><KanbanCards /></Gate>} />
          {/* the section used to live at /buying; change-log entries still link there */}
          <Route path="/buying" element={<Navigate to="/inventory" replace />} />
          <Route path="/plan" element={<Gate any={['screen.landing_board']}><Plan /></Gate>} />
          <Route path="/people" element={<Gate any={['screen.contacts']}><People /></Gate>} />
          <Route path="/settings" element={<Gate any={['screen.settings', 'screen.change_log', 'screen.people']}><Settings /></Gate>} />
          <Route path="/more" element={<More />} />
          <Route path="*" element={<Empty>There is nothing at this address.</Empty>} />
        </Routes>
      </Frame>
      <Toasts />
    </>
  );
}
